import { useEffect } from 'react';
import cn from 'classnames';

import Icon from 'components/generic/Icon.component';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import isEmptyArray from 'utils/predicates/array/isEmptyArray.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import { capitalize } from 'utils/strings/manipulate.util';

import CN from './MultiSelectDropdownInput.module.scss';

/**
 * MultiSelectDropdownInputList component
 * Renders the dropdown list with search, selection counter, and options
 */
const MultiSelectDropdownInputList = ({
  id,
  dropdownClasses,
  listRef,
  searchInputRef,
  searchPlaceholder,
  searchQuery,
  handleSearchChange,
  tempSelected,
  maxSelections,
  selectedText,
  filteredOptions,
  noResultsText,
  isMaxReached,
  handleItemClick,
  selectedValues,
  setTempSelected,
  setSearchQuery,
}) => {
  const { t } = useTranslation();
  // Reset temp selection and search query when dropdown closes (cleanup on unmount)
  useEffect(() => {
    setTempSelected(selectedValues);

    return () => {
      setSearchQuery('');
    };
  }, [selectedValues, setTempSelected, setSearchQuery]);

  return (
    <div id={id} data-bem="MultiSelectDropdownInput__list" className={dropdownClasses} ref={listRef} role="dialog" aria-label={t('multiSelectDropdown.listContainerAriaLabel')}>
      {/* Search input */}
      <div className={CN.searchContainer}>
        <input
          ref={searchInputRef}
          type="text"
          className={CN.searchInput}
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={handleSearchChange}
          onClick={(e) => e.stopPropagation()}
          aria-label={t('multiSelectDropdown.listSearchInputAriaLabel')}
        />
      </div>

      {/* Selection counter */}
      <div className={CN.selectionCounter} aria-live="polite" aria-atomic="true">
        {tempSelected.length}
        {S.space}
        {S.slash}
        {S.space}
        {maxSelections}
        {S.space}
        {selectedText}
      </div>

      {/* Options list */}
      <div className={CN.optionsList} role="listbox" aria-multiselectable="true">
        {isEmptyArray(filteredOptions) ? (
          <div className={CN.noResults} role="status">
            {noResultsText}
          </div>
        ) : (
          filteredOptions.map((item, index) => {
            const itemValue = item.extra || item.description;
            const isSelected = tempSelected.includes(itemValue);
            const isDisabled = !isSelected && isMaxReached;

            return (
              <div
                data-bem="MultiSelectDropdownInput__item"
                key={createNodeKey({ tag: 'item', index, id: itemValue })}
                className={cn({
                  [CN.item]: true,
                  [CN.selectedItem]: isSelected,
                  [CN.disabledItem]: isDisabled,
                })}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onClick={() => !isDisabled && handleItemClick(item)}
              >
                <span className={CN.itemLabel}>{capitalize(item.description)}</span>
                <div className={CN.itemCheckbox} aria-hidden="true">
                  {isSelected ? <Icon variant="radioCheck" selected={true} width="lg" /> : <Icon variant="radioCheck" unselected={true} width="lg" />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
export default MultiSelectDropdownInputList;
